/**
 * Historical P/E chart for one company.
 *
 * Computed entirely from data the report has ALREADY loaded for this stock
 * (its price history and current EPS) — so it adds no extra network calls and
 * only ever covers the one stock you opened. P/E for each past date is that
 * day's close divided by the current trailing EPS, which shows how the
 * valuation multiple has moved as the price moved. EPS is held at its latest
 * value (past EPS by date isn't available from the free data), so this is a
 * "price-driven" P/E band, labelled as such.
 */

import React from "react";
import type { PriceBar } from "@/types";

interface PePoint {
  date: string;
  pe: number;
}

export default function HistoricalPEChart({
  priceBars,
  eps,
}: {
  priceBars: PriceBar[];
  eps: string | number | null;
}): React.ReactElement | null {
  const epsNum = eps === null || eps === undefined ? NaN : Number(eps);

  // Negative or zero EPS makes P/E meaningless (loss-making or no earnings).
  if (!Number.isFinite(epsNum) || epsNum <= 0) {
    return (
      <div className="panel p-6">
        <h2 className="text-lg font-semibold text-mist-100">Historical P/E</h2>
        <p className="mt-2 text-sm text-mist-400">
          {Number.isFinite(epsNum) && epsNum <= 0
            ? "This company has zero or negative earnings, so a P/E ratio isn't meaningful."
            : "Earnings data isn't available yet, so a P/E history can't be shown. Load fundamentals for this stock first."}
        </p>
      </div>
    );
  }

  const points: PePoint[] = priceBars
    .map((bar) => ({ date: bar.price_date, pe: Number(bar.close) / epsNum }))
    .filter((p) => Number.isFinite(p.pe));

  if (points.length < 2) {
    return (
      <div className="panel p-6">
        <h2 className="text-lg font-semibold text-mist-100">Historical P/E</h2>
        <p className="mt-2 text-sm text-mist-400">
          Not enough price history yet to chart the P/E over time.
        </p>
      </div>
    );
  }

  const values = points.map((p) => p.pe);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const current = values[values.length - 1];
  const span = max - min || 1;

  // Chart geometry
  const W = 640;
  const H = 200;
  const padX = 8;
  const padY = 16;
  const x = (i: number): number => padX + (i / (points.length - 1)) * (W - 2 * padX);
  const y = (pe: number): number => padY + (1 - (pe - min) / span) * (H - 2 * padY);

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.pe).toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${x(points.length - 1).toFixed(1)} ${H - padY} L ${x(0).toFixed(1)} ${H - padY} Z`;
  const avgY = y(avg);

  const vsAvgPct = ((current - avg) / avg) * 100;
  const cheapExpensive =
    vsAvgPct > 8 ? "above" : vsAvgPct < -8 ? "below" : "in line with";
  const cheapColor =
    vsAvgPct > 8 ? "text-rose-400" : vsAvgPct < -8 ? "text-emerald-400" : "text-mist-300";

  const firstDate = points[0].date;
  const lastDate = points[points.length - 1].date;

  return (
    <div className="panel p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-mist-100">Historical P/E</h2>
        <span className="text-xs text-mist-500">
          {firstDate} → {lastDate}
        </span>
      </div>
      <p className="mt-1 text-sm text-mist-400">
        How the price-to-earnings multiple has moved. Current P/E is{" "}
        <span className={cheapColor}>
          {Math.abs(vsAvgPct).toFixed(0)}% {cheapExpensive}
        </span>{" "}
        its own {points.length}-day average.
      </p>

      <div className="mt-4 grid grid-cols-4 gap-3 text-center">
        <Stat label="Current" value={current} accent />
        <Stat label="Average" value={avg} />
        <Stat label="Low" value={min} />
        <Stat label="High" value={max} />
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="mt-4 w-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="peFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2FD3A6" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#2FD3A6" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* average reference line */}
        <line
          x1={padX}
          y1={avgY}
          x2={W - padX}
          y2={avgY}
          stroke="#4A5680"
          strokeDasharray="4 4"
          strokeWidth="1"
        />
        <text x={W - padX} y={avgY - 4} textAnchor="end" className="fill-mist-500" fontSize="10">
          avg {avg.toFixed(1)}
        </text>
        <path d={areaPath} fill="url(#peFill)" />
        <path d={linePath} fill="none" stroke="#2FD3A6" strokeWidth="1.5" />
        <circle cx={x(points.length - 1)} cy={y(current)} r="3" fill="#2FD3A6" />
      </svg>

      <p className="mt-3 text-xs leading-relaxed text-mist-500">
        Price-driven estimate: past price ÷ current EPS (historical per-date earnings aren't
        available from the free sources). A trailing-earnings approximation, not exact reported P/E.
      </p>
    </div>
  );
}

function Stat({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: number;
  accent?: boolean;
}): React.ReactElement {
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-900/60 p-3">
      <p className="text-xs uppercase tracking-wide text-mist-500">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${accent ? "text-signal-teal" : "text-mist-100"}`}>
        {value.toFixed(1)}
      </p>
    </div>
  );
}
